import React from "react";
import { Modal, Button, Popconfirm } from "antd";
import { FileOutlined, DeleteOutlined } from "@ant-design/icons";
import { useNavigate } from "react-router-dom";
import type { DocumentMeta } from "../../../services/tareas.api";
import {
  DragDropUpload,
  useClipboardPaste,
} from "../../../Components/DragDropUpload";
import InlineComment from "./InlineComment";

const formatBytes = (bytes: number): string => {
  if (bytes < 1024) return bytes + " B";
  if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(1) + " KB";
  return (bytes / (1024 * 1024)).toFixed(1) + " MB";
};

interface DocsModalProps {
  taskId: number | null;
  docs: DocumentMeta[];
  loading: boolean;
  isAdmin: boolean;
  onClose: () => void;
  onUpload: (file: File) => void;
  onDelete: (key: string) => void;
  /** Actualiza la lista de docs tras guardar un comentario inline. */
  setDocs: React.Dispatch<React.SetStateAction<DocumentMeta[]>>;
}

const DocsModal: React.FC<DocsModalProps> = ({
  taskId,
  docs,
  loading,
  isAdmin,
  onClose,
  onUpload,
  onDelete,
  setDocs,
}) => {
  const navigate = useNavigate();

  // Convención del proyecto: TODA zona de upload soporta paste (Ctrl+V).
  // Activo solo mientras el modal está abierto y el usuario es admin
  // (cliente no sube documentos).
  useClipboardPaste({
    active: taskId !== null && isAdmin,
    onFile: onUpload,
    // Documentos = cualquier tipo. El backend valida MIME/extensión.
  });

  return (
    <Modal
      open={taskId !== null}
      title="Documentos"
      onCancel={onClose}
      destroyOnClose
      footer={null}
      width={640}
    >
      {loading && docs.length === 0 ? (
        <div style={{ textAlign: "center", padding: 24, color: "#999" }}>
          Cargando...
        </div>
      ) : docs.length === 0 ? (
        <div style={{ textAlign: "center", padding: 24, color: "#999" }}>
          No hay documentos para esta tarea.
        </div>
      ) : (
        <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
          {docs.map((doc, idx) => (
            <div
              key={doc.key}
              style={{
                border: "1px solid #f0f0f0",
                borderRadius: 6,
                overflow: "hidden",
              }}
            >
              <div
                style={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                  padding: "8px 12px",
                }}
              >
                <div
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: 8,
                    minWidth: 0,
                  }}
                >
                  <FileOutlined style={{ fontSize: 16, color: "#1677ff" }} />
                  <div style={{ minWidth: 0 }}>
                    <div
                      style={{
                        fontWeight: 500,
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                        whiteSpace: "nowrap",
                      }}
                    >
                      {doc.originalName}
                    </div>
                    <div style={{ fontSize: 12, color: "#999" }}>
                      {formatBytes(doc.size)}
                    </div>
                  </div>
                </div>
                <div style={{ display: "flex", gap: 8, flexShrink: 0 }}>
                  <Button
                    size="small"
                    type="link"
                    onClick={() => navigate(`/visor/tareas/${taskId}/${idx}`)}
                  >
                    Ver
                  </Button>
                  {isAdmin && (
                    <Popconfirm
                      title="Eliminar documento"
                      description="Esta accion eliminara el documento definitivamente. Deseas continuar?"
                      okText="Eliminar"
                      cancelText="Cancelar"
                      okButtonProps={{ danger: true }}
                      onConfirm={() => onDelete(doc.key)}
                    >
                      <Button size="small" danger icon={<DeleteOutlined />} />
                    </Popconfirm>
                  )}
                </div>
              </div>
              <InlineComment
                taskId={taskId}
                attachmentKey={doc.key}
                comment={doc.comentario}
                isAdmin={isAdmin}
                kind="documentos"
                onUpdated={setDocs as any}
              />
            </div>
          ))}
        </div>
      )}

      {/* Zona drag & drop + paste compartida del proyecto. Acepta cualquier
          tipo (el backend valida MIME). Solo visible para admin. */}
      {isAdmin && (
        <div style={{ marginTop: 16 }}>
          <DragDropUpload
            accept="*"
            multiple={false}
            loading={loading}
            onFile={onUpload}
            compact
            title="Arrastra un documento aquí o haz click para seleccionar"
            hint="También puedes pegar con Ctrl+V (archivo copiado del explorador)"
          />
        </div>
      )}
    </Modal>
  );
};

export default DocsModal;
